/* eslint-disable radix */
/* eslint-disable operator-linebreak */
import React, { useContext, useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { format as dateFnsFormat } from 'date-fns';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import TextLabel from '../components/FormComponents/TextLabel';
import Input from '../components/FormComponents/Input';
import Select from '../components/FormComponents/Select';
import Radiobutton from '../components/FormComponents/Radiobutton';
import Checkbox from '../components/FormComponents/Checkbox';
import DateCalendar from '../components/FormComponents/DateCalendar/DateCalendar';
import InputDate from '../components/FormComponents/InputDate';
import { ConnectorContext } from '../../context';
import {
  BTN_ACTIONS,
  getFieldsDefaults,
  localizedUrl,
  applicationStepMessageId,
} from '../../utils';

// The values of a step are kept in memory in applicationData when the step is
// submitted (handleSubmit). Reading them back means Back from an error, such as
// credentials the system rejected, shows what the user typed instead of an
// empty form. Nothing is written to sessionStorage or anywhere else.
export const restoreFormValues = (fieldBlocks, applicationData) => {
  const defaults = getFieldsDefaults(fieldBlocks);

  return Object.keys(defaults).reduce((values, name) => {
    const entered = applicationData?.[name];
    return entered === undefined || entered === null ? values : { ...values, [name]: entered };
  }, defaults);
};

export const VerificationBeforeConnect = () => {
  const {
    state: {
      currentApp: {
        steps,
        applicationLogoUrl,
        name: applicationName,
        code: applicationCode,
        version: applicationVersion,
      },
      applicationData,
    },
    updateState,
  } = useContext(ConnectorContext);
  const navigate = useNavigate();
  const intl = useIntl();
  const { search } = useLocation();
  const { id } = useParams();
  const titleId = applicationStepMessageId(
    intl.messages,
    'connection_step_title',
    applicationCode,
    applicationVersion,
    id
  );
  const stepsLength = Object.keys(steps).length;

  const fieldBlocks = useMemo(
    () =>
      steps[id].reduce(
        (acc, field) => {
          if (field.type === 'button') {
            acc.buttons.push(field);
          } else if (field.type === 'logo') {
            acc.logos.push(field);
          } else {
            acc.main.push(field);
          }

          return acc;
        },
        { main: [], buttons: [], logos: [] }
      ),
    [id]
  );

  const queryParams = new URLSearchParams(useLocation().search);

  const initialFormValues = () => restoreFormValues(fieldBlocks, applicationData);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  // The logo of the system the customer connects to (Blikk, Verified, ...): the
  // logo of the step when it has one (a step of Blikk that is about Verified),
  // otherwise the logo of the application. A logo that does not load is hidden
  // rather than shown as a broken image.
  const stepLogo = fieldBlocks.logos[0];
  const logoUrl = stepLogo?.url || applicationLogoUrl;
  const [brokenLogoUrl, setBrokenLogoUrl] = useState(null);

  useEffect(() => {
    const setQueryParamAsDefaultValue = (fieldName, fieldValue) => {
      const fieldExists = fieldBlocks.main.some((field) => field.name === fieldName);
      // A value the user entered and submitted wins over the one in the link.
      const isEnteredBefore = applicationData?.[fieldName] !== undefined;
      if (fieldExists && !isEnteredBefore) {
        setFormValues((prevState) => ({ ...prevState, [fieldName]: fieldValue }));
      }
    };

    queryParams.forEach((value, name) => {
      setQueryParamAsDefaultValue(name, value);
    });

    const hasRequiredCalendar = fieldBlocks.main.some(
      (field) => field.required && field.type === 'calendar'
    );

    const isCalendarValueEntered = fieldBlocks.main.some(
      (field) => field.type === 'calendar' && formValues[field.name]
    );

    setIsButtonDisabled(
      hasRequiredCalendar && !queryParams.has('invoice_start_date') && !isCalendarValueEntered
    );
  }, [id]);

  const onChange = ({ target: { name, value } }) => {
    setFormValues((prevState) => ({ ...prevState, [name]: value }));
  };

  const onCheckboxChange = ({ target: { name, checked } }) => {
    setFormValues((prevState) => ({ ...prevState, [name]: checked }));
  };

  const formatDateForAPI = (date) => dateFnsFormat(date, 'yyyy-MM-dd');

  const onCalendarChange = (value, name) => {
    const formattedDate = formatDateForAPI(value);
    setFormValues((prevState) => ({ ...prevState, [name]: formattedDate }));
    setIsButtonDisabled(
      !queryParams.has('invoice_start_date') &&
        fieldBlocks.main.some((field) => field.required && field.type === 'calendar') &&
        !formattedDate
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await updateState({ applicationData: { ...applicationData, ...formValues } });

    if (parseInt(id) + 1 === stepsLength) {
      await navigate(`/connect/last-step${search}`);
    } else {
      await navigate(`/connect/step/${parseInt(id) + 1}${search}`);
    }
  };

  const renderButtons = () =>
    fieldBlocks.buttons.map((btn) => {
      if (btn.action === BTN_ACTIONS.NEXT_STEP) {
        return (
          <Button
            key={btn.index}
            variant="contained"
            color="blackBtn"
            type="submit"
            sx={{ marginTop: '10px', color: '#fff' }}
            fullWidth
            disabled={isButtonDisabled}
            id={btn?.name || 'next_btn'}
            data-testid={btn?.name || 'next_btn'}
          >
            <FormattedMessage id={btn.displayName} />
          </Button>
        );
      }

      if (btn.action === BTN_ACTIONS.DOCUMENT) {
        return (
          <Button
            key={btn.index}
            variant="text"
            href={localizedUrl(intl.messages, btn.displayName, btn.url)}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<DescriptionOutlinedIcon />}
            endIcon={<OpenInNewRoundedIcon sx={{ fontSize: '16px !important' }} />}
            sx={{ marginTop: '10px', textTransform: 'none', fontSize: '13px' }}
            fullWidth
            data-testid={btn?.name || 'document_btn'}
          >
            <FormattedMessage id={btn.displayName} />
          </Button>
        );
      }

      if (btn.action === BTN_ACTIONS.BACK) {
        return (
          <Button
            key={btn.index}
            variant="text"
            onClick={() => navigate(`/connect/step/${parseInt(id) - 1}${search}`)}
            sx={{ marginTop: '10px', textTransform: 'none' }}
            fullWidth
            data-testid={btn?.name || 'back_btn'}
          >
            <FormattedMessage id="back" />
          </Button>
        );
      }

      if (btn.action === BTN_ACTIONS.BEACON) {
        return (
          <Button
            key={btn.index}
            variant="outlined"
            sx={{ marginTop: '10px' }}
            data-beacon-article-sidebar={btn.articleId}
            href="#"
            fullWidth
          >
            <FormattedMessage id={btn.displayName} />
          </Button>
        );
      }

      return (
        <Button
          key={btn.index}
          variant="outlined"
          href={btn.url}
          sx={{ marginTop: '10px' }}
          fullWidth
        >
          <FormattedMessage id={btn.displayName} />
        </Button>
      );
    });

  const getFields = (field) => ({
    label: (
      <TextLabel key={field.index} field={field} dataTestid={`${field.type}_${field.index}`} />
    ),
    input: (
      <Input
        key={field.index}
        field={field}
        formValues={formValues}
        onChange={onChange}
        dataTestid={`${field.type}_${field.index}`}
      />
    ),
    input_date: (
      <InputDate
        key={field.index}
        field={field}
        formValues={formValues}
        onChange={onChange}
        format={field.date_format}
        dataTestid={`${field.type}_${field.index}`}
      />
    ),
    select: (
      <Select
        key={field.index}
        field={field}
        formValues={formValues}
        onChange={onChange}
        dataTestid={`${field.type}_${field.index}`}
      />
    ),
    radiobutton: (
      <Radiobutton
        key={field.index}
        field={field}
        formValues={formValues}
        onChange={onChange}
        dataTestid={`${field.type}_${field.index}`}
      />
    ),
    checkbox: (
      <Checkbox
        key={field.index}
        field={field}
        formValues={formValues}
        onChange={onCheckboxChange}
        dataTestid={`${field.type}_${field.index}`}
      />
    ),
    calendar: (
      <DateCalendar
        key={field.index}
        field={field}
        formValues={formValues}
        onChange={(value) => onCalendarChange(value, field.name)}
        dataTestid={`${field.type}_${field.index}`}
      />
    ),
  });

  return (
    <Grid container item justifyContent="center" direction="column" sx={{ margin: '0 auto' }}>
      <Grid item xs sx={{ paddingBottom: '30px' }}>
        {logoUrl && logoUrl !== brokenLogoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt={stepLogo ? '' : applicationName || ''}
            onError={() => setBrokenLogoUrl(logoUrl)}
            sx={{
              display: 'block',
              height: '64px',
              maxWidth: '200px',
              objectFit: 'contain',
              margin: '0 auto 24px',
            }}
            data-testid="application_logo"
          />
        ) : null}
        <Grid item>
          <Typography data-testid="connection_step_title">
            <FormattedMessage id={titleId} />
          </Typography>
        </Grid>

        <form onSubmit={handleSubmit}>
          <Grid item sx={{ padding: '40px 0' }}>
            {fieldBlocks.main.map((field) => getFields(field)[field.type])}
          </Grid>

          <Grid item maxWidth="400px" sx={{ margin: '0 auto' }}>
            {renderButtons()}
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};
